import * as Api from '@ViewModels';
import { useQuery } from '@tanstack/react-query';
import { AIPromptUsage, IAIPromptTemplate } from '../../models/AIPromptTemplate';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { reactQueryClient } from '../../web/ReactQueryProvider';

const BASE_QUERY_KEY = 'template/byType/aiPrompt';

/** template/byType caps a page at 25 whatever pageSize asks for, so a longer list has to follow pageToken. */
const PAGE_SIZE = 25;

/** A stop for a pageToken that never runs out, well past any list a person would scroll. */
const MAX_PAGES = 20;

/**
 * The prompts this account has saved for one surface — the user's own plus anything shared with the account, which
 * is what includeNonUserTemplates adds. Loads every page, because the browser searches what it has loaded.
 */
export function useMyAIPrompts({
	enabled = true,
	impersonationContext,
	usage,
}: {
	enabled?: boolean;
	impersonationContext?: Api.IImpersonationContext;
	usage: AIPromptUsage;
}) {
	const userSession = useUserSession();
	return useQuery({
		enabled,
		queryFn: async () => {
			const prompts: IAIPromptTemplate[] = [];
			let pageToken: string | undefined;
			for (let page = 0; page < MAX_PAGES; page++) {
				const result = await userSession.webServiceHelper.callAsync<Api.IPagedCollection<IAIPromptTemplate>>(
					Api.ImpersonationBroker.composeApiUrl({
						impersonationContext,
						queryParams: {
							includeNonUserTemplates: true,
							pageSize: PAGE_SIZE,
							pageToken,
							type: Api.TemplateType.AIPrompt,
						},
						urlPath: 'template/byType',
					}),
					'GET'
				);
				prompts.push(...(result?.values ?? []));
				pageToken = result?.pageToken;
				if (!pageToken) {
					break;
				}
			}
			return prompts;
		},
		// byType returns saved prompts for every surface and cannot filter on usage, so the pages are cached once for
		// the account and each picker narrows them here, rather than every usage fetching the same pages again.
		queryKey: [BASE_QUERY_KEY, impersonationContext],
		select: prompts => prompts.filter(prompt => prompt.usage === usage),
		meta: {
			toastErrorMessage: 'Failed to load your saved prompts',
		},
	});
}

export const invalidateMyAIPrompts = () => {
	reactQueryClient.invalidateQueries({ queryKey: [BASE_QUERY_KEY] });
};
